/**
 * @author Hiroki Moto
 * @description https://marvelapp.com/4g4b5dg/screen/54237321
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import queryString from 'query-string';
import clone from 'ramda/src/clone';
import concat from 'ramda/src/concat';
import filter from 'ramda/src/filter';
import find from 'ramda/src/find';
import includes from 'ramda/src/includes';
import isNil from 'ramda/src/isNil';
import propEq from 'ramda/src/propEq';
import uniq from 'ramda/src/uniq';
import originalMoment from 'moment';
import { extendMoment } from 'moment-range';
import DatePicker from '@components/@core/DatePicker';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import EditDropdown from '@components/EditDropdown';
import Filter from '@components/@core/Filter';
import ResponsiveTabs from '@components/@core/ResponsiveTabs';
import Tab from '@components/@core/Tab';
import * as AssignmentCourseCard from '@components/AssignmentsCourseCard';
import * as AssignmentQuotaCard from '@components/AssignmentsQuotaCard';
import * as AssignmentsProgramCard from '@components/AssignmentsProgramCard';
import AssignmentsScoreCard from '~/components/AssignmentsScoreCard';
import { AppActions, DevelopActions, ManageActions } from '~/actions';
import { ProgramTypes, SPECIALOPTIONS, AssignmentTabs } from '~/services/config';
import { AssignmentsProgramsPdf } from './AssignmentsProgramsPdf';
import { AssignmentsToDoPdf } from './AssignmentsToDoPdf';
import { AssignmentsTrainingPdf } from './AssignmentsTrainingPdf';
import withNavigate from '~/hocs/withNavigate';

const moment = extendMoment(originalMoment);
const defaultInstance = {
    current_page: 1,
    data: [],
    last_page: 1,
    per_page: 10,
    total: 10,
};

class Assignments extends Component {
    habitscheduleRef = React.createRef();

    state = {
        dateStart: moment().startOf('month'),
        dateEnd: moment().endOf('month'),
        userId: this.props.userId < 0 ? this.props.appId : this.props.userId,
        companyId: this.props.companyId || -1,
        active: queryString.parse(this.props.location?.search)?.active || 'scorecards',
        curPage: 1,
        perPage: 25,
        user: null,
        bulkSelect: false,
        bulks: [],
        selectAll: true,
    };

    static getDerivedStateFromProps(nextProps) {
        const { habits, quotas } = nextProps;

        const habitsData = isNil(habits) ? [] : habits.data;
        const quotasData = isNil(quotas) ? [] : quotas.data;

        const dailyHabits = isNil(habitsData) ? [] : filter((x) => x.data.schedule_interval === 'day', habitsData);
        const weeklyHabits = isNil(habitsData) ? [] : filter((x) => x.data.schedule_interval === 'week', habitsData);
        const monthlyHabits = isNil(habitsData) ? [] : filter((x) => x.data.schedule_interval === 'month', habitsData);

        const scorecardQuotas = isNil(quotasData) ? [] : filter((x) => !isNil(x.scorecard_id), quotasData);
        const programQuotas = isNil(quotasData) ? [] : filter((x) => !isNil(x.program_id), quotasData);

        return { dailyHabits, weeklyHabits, monthlyHabits, scorecardQuotas, programQuotas };
    }

    componentDidMount() {
        const { active, userId, curPage, perPage, dateStart, dateEnd } = this.state;
        const user = find(propEq('id', userId), this.props.employees);
        this.handleFetchSections(active, userId, dateStart, dateEnd);
        this.handleFetchLists(active, userId, curPage, perPage, dateStart, dateEnd);
        const payload = {
            page: curPage,
            perpage: perPage,
            date_range_start: dateStart,
            date_range_end: dateEnd,
        };
        this.props.getTrainingSchedule(payload);
        this.setState({ user });
    }

    handleFetchLists = (type, userId, page, perPage, dateStart, dateEnd) => {
        if (userId < 0) return;
        const order = includes(type, ['programs', 'quotas', 'habits']) ? 'id' : 'due_at';
        const sort = includes(type, ['programs', 'quotas']) ? 'DESC' : 'ASC';
        const payload = {
            page,
            perPage,
            dateStart: dateStart.format('YYYY-MM-DD'),
            dateEnd: dateEnd.format('YYYY-MM-DD'),
            sort,
            order,
        };
        if (type === 'courses') {
            const prevDate = moment(dateStart).subtract(1, 'day').format('YYYY-MM-DD');
            this.props.getFeeds(userId, type, { ...payload, dateStart: null, dateEnd: prevDate }, '', 'assignments');
            this.props.getFeeds(userId, 'courses', { ...payload });
        } else if (type === 'habits') {
            this.props.getFeeds(userId, 'quotas', payload);
            this.props.getFeeds(userId, 'habits', { ...payload, status: [0, 3] });
        } else if (type === 'programs') {
            this.props.getFeeds(userId, type, { ...payload, status: [0, 1, 2, 4, 5] }, '', '');
        } else {
            this.props.getFeeds(userId, 'quotas', payload);
            this.props.getFeeds(userId, type, payload, '', '');
        }
    };

    handleFetchSections = (type, userId, dateStart, dateEnd) => {
        if (userId < 0) return;
        if (type === 'courses') {
            this.props.getFeeds(
                userId,
                'tracks',
                {
                    dateStart: dateStart.format('YYYY-MM-DD'),
                    dateEnd: dateEnd.format('YYYY-MM-DD'),
                    order: 'type',
                    sort: 'DESC',
                },
                'status[]=1&status[]=2'
            );
        } else if (type === 'habits') {
            const payload = {
                dateStart: dateStart.format('YYYY-MM-DD'),
                dateEnd: dateEnd.format('YYYY-MM-DD'),
                order: 'id',
            };
            this.props.getFeeds(userId, 'scorecards', payload);
            this.props.getFeeds(userId, 'programs', payload);
            this.props.getFeeds(userId, 'habitslist', { status: [0, 1, 2], archived: 1 });
        }
    };

    handleFilter = (type, data) => {
        const { dateStart, dateEnd, active, perPage } = this.state;
        if (data.length == 0) return;
        if (type === 'employee') {
            const userId = data[0].id < 0 ? this.props.appId : data[0].id;
            const user = find(propEq('id', userId), this.props.employees);
            if (this.state.userId !== userId) {
                this.handleFetchSections(active, userId, dateStart, dateEnd);
                this.handleFetchLists(active, userId, 1, perPage, dateStart, dateEnd);
                this.setState({ user, userId, curPage: 1 });
            } else this.setState({ user });
        } else {
            const companyId = data[0].id;
            this.setState({ company: data[0], companyId });
        }
    };

    handleSelectTab = (e) => {
        const { userId, curPage, perPage, dateStart, dateEnd } = this.state;
        this.handleFetchSections(e.name, userId, dateStart, dateEnd);
        this.handleFetchLists(e.name, userId, curPage, perPage, dateStart, dateEnd);
        this.setState({ active: e.name, bulkSelect: false, bulks: [], selectAll: true });
        this.props.navigate({ search: `?active=${e.name}` });
    };

    handleDate = (e) => {
        const { active, userId, curPage, perPage } = this.state;
        const dateStart = e.start;
        const dateEnd = e.end;
        this.handleFetchSections(active, userId, dateStart, dateEnd);
        this.handleFetchLists(active, userId, curPage, perPage, dateStart, dateEnd);
        this.setState({ dateStart, dateEnd });
    };

    handlePage = (curPage) => {
        const { userId, dateStart, dateEnd, active, perPage } = this.state;
        this.handleFetchLists(active, userId, curPage, perPage, dateStart, dateEnd);
        this.setState({ curPage });
    };

    handlePer = (perPage) => {
        const { userId, dateStart, dateEnd, active } = this.state;
        let curPage = this.state.curPage;
        if (perPage > 50) curPage = 1;
        this.handleFetchLists(active, userId, curPage, perPage, dateStart, dateEnd);
        this.setState({ perPage });
    };

    handleModal = (card) => {
        const modalData = clone(this.props.modalData);
        const index = 0;
        if (isNil(modalData.before)) modalData.before = {};

        this.props.resetAttactedURL();
        if (isNil(card)) {
            modalData.before.module = modalData.before.course.children[index];
            modalData.before.prevmodule = index !== 0 ? modalData.before.course.children[index - 1] : null;
            modalData.before.index = index;
        } else {
            modalData.before.course = card;
            modalData.before.module = card.children[index];
            modalData.before.prevmodule = index !== 0 ? card.children[index - 1] : null;
            modalData.before.index = index;
        }
        if (!isNil(modalData.before.module)) {
            const { active, curPage, perPage, dateStart, dateEnd } = this.state;
            const userId = this.state.userId < 0 ? this.props.appId : this.state.userId;
            let after = null;
            if (active === 'courses') {
                const prevDate = moment(dateStart).subtract(1, 'day').format('YYYY-MM-DD');
                const payload = {
                    page: curPage,
                    per_page: perPage,
                    date_start: dateStart.format('YYYY-MM-DD'),
                    date_end: dateEnd.format('YYYY-MM-DD'),
                    sort: 'ASC',
                    order: 'due_at',
                };
                after = {
                    type: 'GETLEARNFEEDS_REQUEST',
                    id: userId,
                    target: 'courses',
                    payload,
                    other: '',
                    special: '',
                    after: {
                        type: 'GETLEARNFEEDS_REQUEST',
                        id: userId,
                        target: 'courses',
                        payload: { ...payload, dateStart: null, dateEnd: prevDate },
                        other: '',
                        special: 'assignments',
                    },
                };
            }
            modalData.before.after = after;
            this.props.toggleModal({ type: 'Preview', data: modalData });
        }
    };

    handleSelectMenu = (type) => (event, card) => {
        const { userId, user, curPage, perPage, dateStart, dateEnd, programQuotas } = this.state;
        switch (event) {
            case 'delete':
                if (type === 'courses') {
                    this.props.toggleModal({
                        type: 'Confirm',
                        data: {
                            before: {
                                title: 'Delete',
                                body: 'This will permanently delete the course from your assignments.  Are you sure?',
                            },
                        },
                        callBack: {
                            onYes: () => {
                                this.handleDeleteCourse(card);
                            },
                        },
                    });
                }
                break;

            case 'edit':
                if (type === 'courses') {
                    this.props.toggleModal({
                        type: 'Quick Edit',
                        data: {
                            before: {
                                template: card,
                                type,
                                from: 'instance',
                                after: {
                                    type: 'GETLEARNFEEDS_REQUEST',
                                    id: userId,
                                    target: type,
                                    payload: {
                                        dateStart: dateStart.format('YYYY-MM-DD'),
                                        dateEnd: dateEnd.format('YYYY-MM-DD'),
                                        page: curPage,
                                        perPage,
                                    },
                                    special: 'assignments',
                                },
                            },
                        },
                        callBack: {
                            onDelete: () => this.handleDeleteCourse(card),
                        },
                    });
                } else if (type === 'programs') {
                    this.props.toggleModal({
                        type: 'Quick Edit',
                        data: {
                            before: {
                                template: card,
                                type,
                                from: 'instance',
                                after: {
                                    type: 'GETLEARNFEEDS_REQUEST',
                                    id: userId,
                                    target: 'quotas',
                                    payload: {
                                        page: curPage,
                                        perPage,
                                        dateStart: dateStart.format('YYYY-MM-DD'),
                                        dateEnd: dateEnd.format('YYYY-MM-DD'),
                                        order: 'id',
                                    },
                                },
                            },
                        },
                        callBack: {
                            onDelete: (e) => this.handleDeleteProgram(e),
                        },
                    });
                } else if (type === 'habits') {
                    const _after = {
                        type: 'GETTASKSFEED_REQUEST',
                        userId: card.user_id,
                        payload: {
                            page: curPage,
                            perPage,
                        },
                    };
                    this.props.toggleModal({
                        type: 'Edit Task',
                        data: {
                            before: {
                                card,
                                after: _after,
                            },
                        },
                    });
                }
                break;

            case 'assign':
                {
                    let payload = {};
                    if (type === 'courses') {
                        payload = {
                            type: 'Assign Training',
                            data: {
                                before: {
                                    assignees: [userId],
                                    disabled: ['tracks'],
                                    companyId: user.company_id,
                                    after: {
                                        type: 'GETLEARNFEEDS_REQUEST',
                                        id: userId,
                                        target: type,
                                        payload: {
                                            dateStart: dateStart.format('YYYY-MM-DD'),
                                            dateEnd: dateEnd.format('YYYY-MM-DD'),
                                            page: curPage,
                                            perPage,
                                            order: 'due_at',
                                            sort: 'ASC',
                                        },
                                    },
                                },
                            },
                        };
                    } else if (type === 'habits') {
                        payload = {
                            type: 'Assign ToDo',
                            data: {
                                before: {
                                    disabled: ['habitslist', 'scorecards', 'quotas'],
                                    companyId: user.company_id,
                                },
                            },
                        };
                    } else if (type === 'quotas') {
                        payload = {
                            type: 'Assign ToDo',
                            data: {
                                before: {
                                    disabled: ['habits', 'habitslist', 'scorecards'],
                                    companyId: user.company_id,
                                },
                            },
                        };
                    } else if (type === 'programs') {
                        const afterPayload = {
                            dateStart: dateStart.format('YYYY-MM-DD'),
                            dateEnd: dateEnd.format('YYYY-MM-DD'),
                            page: curPage,
                            perPage,
                            order: 'id',
                        };
                        payload = {
                            type: 'Assign Program',
                            data: {
                                before: {
                                    disabled: [],
                                    companyId: user.company_id,
                                    after: {
                                        type: 'GETLEARNFEEDS_REQUEST',
                                        id: userId,
                                        target: 'quotas',
                                        payload: afterPayload,
                                        after: {
                                            type: 'GETLEARNFEEDS_REQUEST',
                                            id: userId,
                                            target: 'programs',
                                            payload: afterPayload,
                                        },
                                    },
                                },
                            },
                        };
                    } else if (type === 'scorecards') {
                        payload = {
                            type: 'Assign ToDo',
                            data: {
                                before: {
                                    assignees: [userId],
                                    disabled: ['habitslist', 'habits', 'quotas'],
                                    companyId: user.company_id,
                                    after: {
                                        type: 'GETLEARNFEEDS_REQUEST',
                                        id: userId,
                                        target: 'scorecards',
                                        payload: {
                                            dateStart: dateStart.format('YYYY-MM-DD'),
                                            dateEnd: dateEnd.format('YYYY-MM-DD'),
                                            page: curPage,
                                            perPage,
                                            order: 'id',
                                        },
                                    },
                                },
                            },
                        };
                    } else if (type === 'habitslists') {
                        payload = {
                            type: 'Assign ToDo',
                            data: {
                                before: {
                                    disabled: ['scorecards', 'habits', 'quotas'],
                                    companyId: user.company_id,
                                    after: {
                                        type: 'GETLEARNFEEDS_REQUEST',
                                        id: userId,
                                        target: 'habitslist',
                                        payload: {
                                            status: [0, 1, 2],
                                            archived: 1,
                                            order: 'due_at',
                                        },
                                        after: {
                                            type: 'GETLEARNFEEDS_REQUEST',
                                            id: userId,
                                            target: 'habits',
                                            payload: {
                                                dateStart: dateStart.format('YYYY-MM-DD'),
                                                dateEnd: dateEnd.format('YYYY-MM-DD'),
                                                page: curPage,
                                                perPage,
                                                status: [0, 3],
                                                order: 'id',
                                            },
                                        },
                                    },
                                },
                            },
                        };
                    }

                    this.props.toggleModal(payload);
                }
                break;

            case 'detail view':
                if (type === 'courses') {
                    this.props.navigate({
                        pathname: `/library/training/courses/${card.card_template_id}/view`,
                        state: { card },
                    });
                } else if (type === 'habits') {
                    this.props.toggleModal({
                        type: 'Task Detail',
                        data: {
                            before: {
                                card,
                                after: {
                                    type: 'GETLEARNFEEDS_REQUEST',
                                    id: userId,
                                    target: 'habits',
                                    payload: {
                                        dateStart: dateStart.format('YYYY-MM-DD'),
                                        dateEnd: dateEnd.format('YYYY-MM-DD'),
                                        page: curPage,
                                        perPage,
                                        order: 'id',
                                        sort: 'ASC',
                                        status: [0, 3],
                                    },
                                },
                            },
                        },
                    });
                } else if (type === 'quotas') {
                    this.props.navigate(`/library/to-do/quotas/${card.quota_template_id}/view`);
                } else if (type === 'programs') {
                    this.handleOpenProgramDetail(card);
                }
                break;

            case 'quick assign':
                this.props.toggleModal({
                    type: 'Quick Assign',
                    data: { before: { template: card, type: 'Module', companyId: user.company_id } },
                });
                break;

            case 'edit assignment':
                {
                    const { scorecards, programs, habitslist } = this.props;
                    if (type === 'scorecards') {
                        this.props.toggleModal({
                            type: 'Quick Edit',
                            data: {
                                before: {
                                    template: scorecards.data,
                                    type,
                                    from: 'instance',
                                    after: {
                                        type: 'GETLEARNFEEDS_REQUEST',
                                        id: userId,
                                        target: type,
                                        payload: {
                                            dateStart: dateStart.format('YYYY-MM-DD'),
                                            dateEnd: dateEnd.format('YYYY-MM-DD'),
                                            page: curPage,
                                            perPage,
                                            order: 'id',
                                        },
                                    },
                                },
                            },
                            callBack: {
                                onDelete: (e) => this.handleDeleteScorecard(e),
                            },
                        });
                    } else if (type === 'programs') {
                        this.props.toggleModal({
                            type: 'Quick Edit',
                            data: {
                                before: {
                                    template: programs.data,
                                    type,
                                    from: 'instance',
                                    after: {
                                        type: 'GETLEARNFEEDS_REQUEST',
                                        id: userId,
                                        target: 'quotas',
                                        payload: {
                                            dateStart: dateStart.format('YYYY-MM-DD'),
                                            dateEnd: dateEnd.format('YYYY-MM-DD'),
                                            page: curPage,
                                            perPage,
                                            order: 'id',
                                        },
                                    },
                                },
                            },
                            callBack: {
                                onDelete: (e) => this.handleDeleteProgram(e),
                            },
                        });
                    } else if (type === 'habitslists') {
                        this.props.toggleModal({
                            type: 'Quick Edit',
                            data: {
                                before: {
                                    template: habitslist.data,
                                    type: 'habitslist',
                                    from: 'instance',
                                    after: {
                                        type: 'GETLEARNFEEDS_REQUEST',
                                        id: userId,
                                        target: type,
                                        payload: {
                                            dateStart: dateStart.format('YYYY-MM-DD'),
                                            dateEnd: dateEnd.format('YYYY-MM-DD'),
                                            page: curPage,
                                            perPage,
                                            order: 'id',
                                        },
                                    },
                                },
                            },
                            callBack: {
                                onDelete: (e) => this.hanldeDeleteHabitschedule(e),
                            },
                        });
                    } else if (type === 'habits') {
                        this.props.toggleModal({
                            type: 'Quick Edit',
                            data: {
                                before: {
                                    template: card,
                                    type,
                                    from: 'instance',
                                    after: {
                                        type: 'GETLEARNFEEDS_REQUEST',
                                        id: userId,
                                        target: 'quotas',
                                        payload: {
                                            page: curPage,
                                            perPage,
                                            dateStart: dateStart.format('YYYY-MM-DD'),
                                            dateEnd: dateEnd.format('YYYY-MM-DD'),
                                            order: 'id',
                                        },
                                    },
                                },
                            },
                            callBack: {
                                onDelete: (e) => this.hanldeDeleteHabitschedule(e),
                            },
                        });
                    }
                }
                break;

            case 'edit actuals':
                {
                    if (type === 'scorecards') {
                        this.props.toggleModal({
                            type: 'Save Actuals',
                            data: {
                                before: {
                                    user,
                                    type,
                                    after: {
                                        type: 'GETLEARNFEEDS_REQUEST',
                                        id: userId,
                                        target: type,
                                        payload: {
                                            dateStart: dateStart.format('YYYY-MM-DD'),
                                            dateEnd: dateEnd.format('YYYY-MM-DD'),
                                            page: curPage,
                                            perPage,
                                            order: 'id',
                                        },
                                    },
                                },
                            },
                            callBack: {
                                onRefresh: () => {
                                    const { active, userId, curPage, perPage, dateStart, dateEnd } = this.state;
                                    this.handleFetchSections(active, userId, dateStart, dateEnd);
                                    this.handleFetchLists(active, userId, curPage, perPage, dateStart, dateEnd);
                                },
                            },
                        });
                    } else if (type === 'programs') {
                        if (programQuotas?.length === 0) {
                            toast.warn(`This program doesn't have any QUOTAs that you can update!`, {
                                position: toast.POSITION.TOP_RIGHT,
                            });
                        } else {
                            this.props.toggleModal({
                                type: 'Save Actuals',
                                data: {
                                    programQuotas,
                                    before: {
                                        user,
                                        type,
                                        after: {
                                            type: 'GETLEARNFEEDS_REQUEST',
                                            id: userId,
                                            target: 'quotas',
                                            payload: {
                                                dateStart: dateStart.format('YYYY-MM-DD'),
                                                dateEnd: dateEnd.format('YYYY-MM-DD'),
                                                page: curPage,
                                                perPage,
                                                order: 'id',
                                            },
                                        },
                                    },
                                },
                                callBack: {
                                    onRefresh: () => {
                                        const { active, userId, curPage, perPage, dateStart, dateEnd } = this.state;
                                        this.handleFetchSections(active, userId, dateStart, dateEnd);
                                        this.handleFetchLists(active, userId, curPage, perPage, dateStart, dateEnd);
                                    },
                                },
                            });
                        }
                    } else {
                        this.props.toggleModal({
                            type: 'Save Actuals',
                            data: {
                                before: {
                                    user,
                                    type,
                                    after: {
                                        type: 'GETLEARNFEEDS_REQUEST',
                                        user,
                                        id: userId,
                                        target: 'quotas',
                                        payload: {
                                            dateStart: dateStart.format('YYYY-MM-DD'),
                                            dateEnd: dateEnd.format('YYYY-MM-DD'),
                                            page: curPage,
                                            perPage,
                                            order: 'id',
                                        },
                                    },
                                },
                            },
                            callBack: {
                                onRefresh: () => {
                                    const { active, userId, curPage, perPage, dateStart, dateEnd } = this.state;
                                    this.handleFetchSections(active, userId, dateStart, dateEnd);
                                    this.handleFetchLists(active, userId, curPage, perPage, dateStart, dateEnd);
                                },
                            },
                        });
                    }
                }
                break;

            case 'preview view':
                this.handleModal(card);
                break;

            case 'bulk unassign':
                this.setState({ bulkSelect: true });
                break;

            default:
                break;
        }
    };

    hanldeDeleteHabitschedule = (habitslist) => {
        const { userId, perPage, curPage, dateStart, dateEnd } = this.state;
        this.props.deleteCard({
            event: 'delete',
            cardId: habitslist.id,
            card: habitslist,
            after: {
                type: 'GETLEARNFEEDS_REQUEST',
                id: userId,
                target: 'habitslist',
                payload: {
                    status: [0, 1, 2],
                    archived: 1,
                    order: 'due_at',
                },
                after: {
                    type: 'GETLEARNFEEDS_REQUEST',
                    id: userId,
                    target: 'habits',
                    payload: {
                        page: curPage,
                        perPage,
                        dateStart: dateStart.format('YYYY-MM-DD'),
                        dateEnd: dateEnd.format('YYYY-MM-DD'),
                        sort: 'ASC',
                        order: 'id',
                        status: [0, 3],
                    },
                },
            },
        });
    };

    handleDeleteCourse = (course) => {
        const { userId, perPage, curPage, dateStart, dateEnd, active } = this.state;
        const prevDate = moment(dateStart).subtract(1, 'day').format('YYYY-MM-DD');
        this.props.deleteCard({
            event: 'delete',
            cardId: course.id,
            card: course,
            after: {
                type: 'GETLEARNFEEDS_REQUEST',
                id: userId,
                target: active,
                payload: {
                    dateStart: null,
                    dateEnd: prevDate,
                    page: curPage,
                    perPage,
                },
                special: 'assignments',
                after: {
                    type: 'GETLEARNFEEDS_REQUEST',
                    id: userId,
                    target: active,
                    payload: {
                        dateStart: dateStart.format('YYYY-MM-DD'),
                        dateEnd: dateEnd.format('YYYY-MM-DD'),
                        page: curPage,
                        perPage,
                    },
                },
            },
        });
    };

    handleDeleteScorecard = (card) => {
        const { userId, curPage, perPage, dateStart, dateEnd } = this.state;
        const payload = { scorecard: { id: card.id } };
        const after = {
            type: 'GETLEARNFEEDS_REQUEST',
            id: userId,
            target: 'scorecards',
            payload: {
                dateStart: dateStart.format('YYYY-MM-DD'),
                dateEnd: dateEnd.format('YYYY-MM-DD'),
                page: curPage,
                perPage,
                order: 'id',
            },
        };
        this.props.deleteScorecard(payload, after);
    };

    handleDeleteProgram = (card) => {
        const { userId, curPage, perPage, dateStart, dateEnd } = this.state;
        const afterPayload = {
            dateStart: dateStart.format('YYYY-MM-DD'),
            dateEnd: dateEnd.format('YYYY-MM-DD'),
            page: curPage,
            perPage,
            order: 'id',
        };
        const payload = {
            event: 'stop',
            data: { user_id: [userId], program_id: card.id },
            after: {
                type: 'GETLEARNFEEDS_REQUEST',
                id: userId,
                target: 'programs',
                payload: afterPayload,
                after: {
                    type: 'GETLEARNFEEDS_REQUEST',
                    id: userId,
                    target: 'quotas',
                    payload: afterPayload,
                },
            },
        };
        this.props.deleteProgram(payload);
    };

    handleOpenScheduleDetail = (card) => {
        const { type } = card;
        if (type === 3) {
            this.props.navigate({
                pathname: `/hcm/report-training-schedule/${card.id}`,
                state: { editable: false, schedule: card },
            });
        }
    };

    handleOpenScorecardDetail = (scorecard) => {
        const { dateStart, dateEnd } = this.state;
        const date = moment.range(dateStart, dateEnd);

        this.props.toggleModal({
            type: 'Scorecard Detail',
            data: { before: { scorecard, date } },
        });
    };

    handleOpenProgramDetail = (card) => {
        const { id, type, user_id } = card;
        if (ProgramTypes[type - 1] === 'careers') {
            this.props.getCareerProgram(id, `/hcm/report-${ProgramTypes[type - 1]}/employee/${user_id}/view`);
        } else if (ProgramTypes[type - 1] === 'certifications') {
            this.props.getCertificationDetail(card, 'certifications', `/hcm/report-${ProgramTypes[type - 1]}/${user_id}/${id}/view`);
        }
    };

    handleOpenHabitScheduleDetail = (card) => {
        const { card_template_id } = card;
        this.props.navigate({
            pathname: `/library/to-do/habitslist/${card_template_id}/view`,
            state: { editable: false, card },
        });
    };

    handleSelectSectionMenu = (event, schedules, tracks) => {
        const { userId, user, dateStart, dateEnd, curPage, perPage } = this.state;

        const afterDates = {
            date_start: dateStart.format('YYYY-MM-DD'),
            date_end: dateEnd.format('YYYY-MM-DD'),
        };

        switch (event) {
            case 'assign':
                this.props.toggleModal({
                    type: 'Assign Training',
                    data: {
                        before: {
                            assignees: [this.state.userId],
                            disabled: ['courses'],
                            companyId: user.company_id,
                        },
                        after: null,
                    },
                });
                break;

            case 'unassign':
                this.props.toggleModal({
                    type: 'Advanced Edit',
                    data: {
                        before: {
                            userId,
                            schedules,
                            tracks,
                            after: {
                                type: 'GETLEARNFEEDS_REQUEST',
                                id: userId,
                                target: 'tracks',
                                payload: { ...afterDates, order: 'type', sort: 'DESC' },
                                other: 'status[]=1&status[]=2',
                                after: {
                                    type: 'GETLEARNFEEDS_REQUEST',
                                    id: userId,
                                    target: 'courses',
                                    payload: { ...afterDates, page: curPage, perPage, order: 'due_at', sort: 'ASC' },
                                },
                            },
                        },
                    },
                });
                break;

            default:
                break;
        }
    };

    handleAssignTraining = (e) => {
        const { userId, curPage, perPage, dateStart, dateEnd, user, active } = this.state;
        const { templates, due_date, type } = e;
        const payload = { user_id: [userId], card_type: type, due_date, company_id: user.company_id };

        const afterPayload = {
            dateStart: dateStart.format('YYYY-MM-DD'),
            dateEnd: dateEnd.format('YYYY-MM-DD'),
            page: curPage,
            perPage,
        };

        const after = {
            type: 'GETLEARNFEEDS_REQUEST',
            id: userId,
            target: 'tracks',
            payload: { ...afterPayload, order: 'type', sort: 'DESC' },
            other: 'status[]=1&status[]=2',
            after: {
                type: 'GETLEARNFEEDS_REQUEST',
                id: userId,
                target: active,
                payload: { ...afterPayload, order: 'due_at', sort: 'ASC' },
            },
        };
        this.props.assignTraining(type === 'tracks' ? { ...payload, track_id: templates } : { ...payload, card_template_id: templates }, after);
    };

    handleUnassign = (e) => {
        const { userId, curPage, perPage, dateStart, dateEnd, active } = this.state;

        if (active === 'courses') {
            if (isNil(e.card_type_id) && isNil(e.program_id)) {
                const payload = {
                    data: {
                        unassign: [{ user_id: 2, track_id: [e.id] }],
                    },
                    after: {
                        type: 'GETLEARNFEEDS_REQUEST',
                        id: userId,
                        target: 'tracks',
                        payload: {
                            dateStart: dateStart.format('YYYY-MM-DD'),
                            dateEnd: dateEnd.format('YYYY-MM-DD'),
                            page: curPage,
                            perPage,
                            order: 'type',
                            sort: 'DESC',
                        },
                        other: 'status[]=1&status[]=2',
                    },
                };
                this.props.unassign(payload);
            }
        }
    };

    handleBulkCheck = (course, checked) => {
        let bulks = clone(this.state.bulks);
        if (checked) bulks.push(course.id);
        else bulks = filter((x) => x !== course.id, bulks);
        bulks = uniq(bulks);
        this.setState({ bulks });
    };

    handleCancelBulk = () => {
        this.setState({ bulkSelect: false, bulks: [] });
    };

    handleUnassignBulks = () => {
        const { userId, bulks, curPage, perPage, dateStart, dateEnd, active } = this.state;

        const prevDate = moment(dateStart).subtract(1, 'day').format('YYYY-MM-DD');

        const unassignPayload = {
            unassign: [
                {
                    user_id: userId < 0 ? this.props.appId : userId,
                    card_instance_id: bulks,
                    track_id: [],
                    schedule_id: [],
                    program_id: [],
                },
            ],
        };
        const payload = {
            dateStart: dateStart.format('YYYY-MM-DD'),
            dateEnd: dateEnd.format('YYYY-MM-DD'),
            page: curPage,
            perPage,
            order: 'due_at',
            sort: 'ASC',
        };

        const after = {
            type: 'GETLEARNFEEDS_REQUEST',
            id: userId,
            target: active,
            payload,
            after: {
                type: 'GETLEARNFEEDS_REQUEST',
                id: userId,
                target: active,
                payload: { ...payload, dateStart: null, dateEnd: prevDate },
                special: 'assignments',
            },
        };

        this.props.unassign({ data: unassignPayload, after });

        this.handleCancelBulk();
    };

    handleSelectAll = (type) => () => {
        const { courses, carryOvers, habits } = this.props;
        const { selectAll } = this.state;
        if (type === 'courses') {
            if (selectAll) {
                let bulks = concat(courses.data, carryOvers.data);
                bulks = filter((x) => isNil(x.program_id), bulks);
                bulks = bulks.map((e) => e.id);
                this.setState({ bulks, selectAll: !selectAll });
            } else {
                this.setState({ bulks: [], selectAll: !selectAll });
            }
        } else if (type === 'habits') {
            if (selectAll) {
                let bulks = habits.data;
                bulks = filter((x) => isNil(x.program_id), bulks);
                bulks = bulks.map((e) => e.id);
                this.setState({ bulks, selectAll: !selectAll });
            } else {
                this.setState({ bulks: [], selectAll: !selectAll });
            }
        }
    };

    handleHabitCheck = (e, id) => {
        const { userId, curPage, perPage, dateStart, dateEnd } = this.state;
        const after = {
            type: 'GETLEARNFEEDS_REQUEST',
            id: userId,
            target: 'habits',
            payload: {
                dateStart: dateStart.format('YYYY-MM-DD'),
                dateEnd: dateEnd.format('YYYY-MM-DD'),
                page: curPage,
                perPage,
                order: 'id',
                sort: 'ASC',
                status: [0, 3],
            },
        };
        this.props.updateHabit(e, id, after);
    };

    handleReportMenu = (e) => {
        const { active, companyId, dateStart, dateEnd, dailyHabits, weeklyHabits, monthlyHabits, programQuotas, userId } = this.state;
        const { courses, carryOvers, programs, tracks, scorecards, habitslist } = this.props;
        const date = moment.range(dateStart, dateEnd);
        switch (e) {
            case 'print':
                if (active === 'courses') {
                    const trackData = filter(propEq('type', 1), tracks.data);
                    const scheduleData = filter(propEq('type', 3), tracks.data);
                    const trainingPdf = {
                        date,
                        userId,
                        tracks: trackData,
                        schedules: scheduleData,
                        assignments: carryOvers.data,
                        trainings: courses.data,
                    };
                    AssignmentsTrainingPdf(trainingPdf);
                } else if (active === 'habits') {
                    const scorecard = filter((e) => companyId !== -1 && e.company_id == companyId, scorecards.data);
                    const todoPdf = {
                        date,
                        userId,
                        scorecards: scorecard,
                        programs: programs.data,
                        quotas: programQuotas,
                        habitSchedules: habitslist.data,
                        dailyHabits,
                        weeklyHabits,
                        monthlyHabits,
                    };
                    AssignmentsToDoPdf(todoPdf);
                } else if (active === 'programs') {
                    const data = filter((program) => program.status !== 3, programs.data);
                    const programsPdf = { date, data };
                    AssignmentsProgramsPdf(programsPdf);
                }
                break;
        }
    };

    render() {
        const { role, courses, carryOvers, programs, tracks, scorecards, trainingSchedules } = this.props;
        const { userId, companyId, dateStart, dateEnd, programQuotas, bulkSelect, bulks, active } = this.state;
        const date = moment.range(dateStart, dateEnd);
        const trackData = filter(propEq('type', 1), tracks.data);
        const scheduleData = filter(propEq('type', 3), trainingSchedules.data);

        return (
            <ErrorBoundary className="dev-assignment-list">
                <Filter onChange={this.handleFilter} />
                <div className="card-header d-h-between">
                    <span className="dsl-b22 bold d-flex-1">Assignments</span>
                    {active !== 'programs' && <DatePicker calendar="range" append="caret" format="MMM D" as="span" align="right" value={date} closeAfterSelect onSelect={this.handleDate} />}
                    <div className="d-h-end cursor-pointer ms-3">
                        <EditDropdown className="ml-3" dataCy="editDropdown" options={['Print']} onChange={this.handleReportMenu} />
                    </div>
                </div>
                <ResponsiveTabs className="card-content" active={active} data={AssignmentTabs} getId={(e) => e['name']} getValue={(e) => e['label']} onTab={this.handleSelectTab} />
                <Tab eventKey="courses" active={active}>
                    <AssignmentCourseCard.Section
                        dataCy="scheduleAndTrack"
                        userRole={role}
                        tracks={trackData}
                        schedules={scheduleData}
                        onDetail={this.handleOpenScheduleDetail}
                        onSelect={this.handleSelectSectionMenu}
                    />
                    <AssignmentCourseCard.List
                        bulkSelect={bulkSelect}
                        bulkList={bulks}
                        assignments={carryOvers.data}
                        trainings={courses.data}
                        current={courses.current_page}
                        perPage={courses.per_page}
                        total={courses.last_page}
                        userId={userId}
                        userRole={role}
                        onSelect={this.handleSelectMenu('courses')}
                        onBulkSelect={this.handleBulkCheck}
                        onCancelBulk={this.handleCancelBulk}
                        onSelectAllBulks={this.handleSelectAll('courses')}
                        onUnassignBulks={this.handleUnassignBulks}
                        onPage={this.handlePage}
                        onPer={this.handlePer}
                        onModal={this.handleModal}
                    />
                </Tab>
                <Tab eventKey="habits" active={active}>
                    <AssignmentQuotaCard.ScorecardsSection
                        userRole={role}
                        company={companyId}
                        scorecards={scorecards.data}
                        onDetail={this.handleOpenScorecardDetail}
                        onSelect={this.handleSelectMenu('quotas')}
                        onSelectMenu={this.handleSelectMenu('scorecards')}
                    />
                    <AssignmentQuotaCard.ProgramsSection
                        userRole={role}
                        quotas={programQuotas}
                        programs={programs.data}
                        onDetail={this.handleOpenProgramDetail}
                        onSelect={this.handleSelectMenu('quotas')}
                        onSelectMenu={this.handleSelectMenu('programs')}
                    />
                </Tab>
                <Tab eventKey="scorecards" active={active}>
                    <AssignmentsScoreCard role={role} onDetail={this.handleOpenScorecardDetail} onSelectMenu={this.handleSelectMenu('scorecards')} />
                </Tab>
                <Tab eventKey="programs" active={active}>
                    <AssignmentsProgramCard.List
                        userRole={role}
                        data={programs.data}
                        current={programs.current_page}
                        perPage={programs.per_page}
                        total={programs.last_page}
                        onChange={this.handleChangePage}
                        onSelect={this.handleSelectMenu('programs')}
                    />
                </Tab>
            </ErrorBoundary>
        );
    }
}

Assignments.propTypes = {
    role: PropTypes.number,
    appId: PropTypes.number,
    userId: PropTypes.number,
    employees: PropTypes.array,
    trainings: PropTypes.any,
    assignments: PropTypes.any,
    courses: PropTypes.shape({
        current_page: PropTypes.number,
        data: PropTypes.array,
        last_page: PropTypes.number,
        per_page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        total: PropTypes.number,
    }),
    carryOvers: PropTypes.shape({
        current_page: PropTypes.number,
        data: PropTypes.array,
        last_page: PropTypes.number,
        per_page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        total: PropTypes.number,
    }),
    programs: PropTypes.shape({
        current_page: PropTypes.number,
        data: PropTypes.array,
        last_page: PropTypes.number,
        per_page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        total: PropTypes.number,
    }),
    tracks: PropTypes.shape({
        current_page: PropTypes.number,
        data: PropTypes.array,
        last_page: PropTypes.number,
        per_page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        total: PropTypes.number,
    }),
    scorecards: PropTypes.shape({
        current_page: PropTypes.number,
        data: PropTypes.array,
        last_page: PropTypes.number,
        per_page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        total: PropTypes.number,
    }),
    habitslist: PropTypes.shape({
        current_page: PropTypes.number,
        data: PropTypes.array,
        last_page: PropTypes.number,
        per_page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        total: PropTypes.number,
    }),
    modalData: PropTypes.any,
    deleteCard: PropTypes.func,
    getFeeds: PropTypes.func,
    toggleModal: PropTypes.func,
    resetAttactedURL: PropTypes.func,
    getCertificationDetail: PropTypes.func,
    assignTraining: PropTypes.func,
    unassign: PropTypes.func,
    getTrainingSchedule: PropTypes.func,
};

Assignments.defaultProps = {
    role: 1,
    appId: 1,
    userId: 1,
    employees: [],
    trainings: {},
    assignments: {},
    courses: defaultInstance,
    carryOvers: defaultInstance,
    programs: defaultInstance,
    tracks: defaultInstance,
    scorecards: defaultInstance,
    habitslist: defaultInstance,
    modalData: {},
    toggleModal: () => {},
    getFeeds: () => {},
    deleteCard: () => {},
    getCertificationDetail: () => {},
    assignTraining: () => {},
    unassign: () => {},
    getTrainingSchedule: () => {},
};

const mapStateToProps = (state) => ({
    appId: state.app.id,
    userId: state.app.selectedEmployee.users[0],
    companyId: state.app.selectedCompany[0],
    employees: state.app.users,
    role: state.app.app_role_id,
    trainingSchedules: state.develop.trainingSchedules,
    trainings: state.develop.templates.trainings,
    assignments: state.develop.templates.assignments,
    carryOvers: state.develop.instances.carryOvers,
    courses: state.develop.instances.courses,
    habits: state.develop.instances.habits,
    quotas: state.develop.instances.quotas,
    programs: state.develop.instances.programs,
    tracks: state.develop.instances.tracks,
    scorecards: state.develop.instances.scorecards,
    habitslist: state.develop.instances.habitslist,
    modalData: state.app.modalData,
});

const mapDispatchToProps = (dispatch) => ({
    getFeeds: (id, target, e, other, s) => dispatch(DevelopActions.getlearnfeedsRequest(id, target, e, other, s)),
    deleteCard: (e) => dispatch(DevelopActions.updatecardinstanceRequest(e)),
    deleteScorecard: (e, after) => dispatch(ManageActions.postdeletescorecardRequest(e, after)),
    deleteProgram: (e) => dispatch(DevelopActions.programeventRequest(e)),
    resetAttactedURL: () => dispatch(AppActions.uploadSuccess('')),
    toggleModal: (payload) => dispatch(AppActions.modalRequest(payload)),
    getCertificationDetail: (e, mode, route) => dispatch(DevelopActions.libraryprogramdetailRequest(e, mode, route)),
    getCareerProgram: (id, route) => dispatch(ManageActions.getcareerprogramRequest(id, route)),
    assignTraining: (payload, after) => dispatch(ManageActions.assigntrainingRequest(payload, after)),
    updateHabit: (event, cardId, after) => dispatch(ManageActions.updatetaskRequest(event, cardId, after)),
    unassign: (payload) => dispatch(ManageActions.unassigncontentRequest(payload)),
    getTrainingSchedule: (e) => dispatch(DevelopActions.traininglistRequest(e)),
});

const AssignmentsWithNavigate = withNavigate(Assignments);

export default connect(mapStateToProps, mapDispatchToProps)(AssignmentsWithNavigate);
